import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  EuiBadge,
  EuiBasicTable,
  EuiButton,
  EuiCallOut,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPopover,
  EuiProgress,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import RECIPE_QA from "../querys/recipeQA";

const SEVERITY_COLOR = { high: "danger", medium: "warning", low: "default" };

const ENGINES = {
  llm: { label: "LLM (OpenAI + rules)", icon: "editorComment" },
  typesafe: { label: "TypeSafe (Jev)", icon: "visGauge" },
};

const probabilityColumn = {
  field: "probability",
  name: "Probability",
  width: "100px",
  render: (p) => (p === null || p === undefined ? "-" : `${Math.round(p * 100)}%`),
};

const baseColumns = [
  {
    field: "severity",
    name: "Severity",
    width: "90px",
    render: (severity) => (
      <EuiBadge color={SEVERITY_COLOR[severity] || "default"}>{severity}</EuiBadge>
    ),
  },
  {
    field: "category",
    name: "Category",
    width: "190px",
    render: (category, item) => (
      <span>
        {category}{" "}
        <EuiBadge color="hollow">{item.source}</EuiBadge>
      </span>
    ),
  },
  { field: "reason", name: "Reason" },
  {
    field: "quote",
    name: "Evidence",
    render: (quote, item) =>
      quote && item.source === "typesafe" ? (
        <EuiText size="s">
          <em>“{quote}”</em>
        </EuiText>
      ) : quote ? (
        <EuiToolTip
          content={
            item.quoteFound
              ? "Found in the recipe text"
              : "Not found verbatim in the recipe - possible hallucination"
          }
        >
          <EuiText size="s" color={item.quoteFound ? "default" : "danger"}>
            <em>“{quote}”</em>
          </EuiText>
        </EuiToolTip>
      ) : (
        "-"
      ),
  },
];

export default function RecipeQAButton({ id }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [engine, setEngine] = useState("llm");
  const [runQA, { loading, data, error, refetch }] = useLazyQuery(RECIPE_QA, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const open = (selected) => {
    setIsPopoverOpen(false);
    setEngine(selected);
    setIsModalVisible(true);
    runQA({ variables: { id, engine: selected } });
  };

  const result = data && data.recipeQa;
  const columns =
    result && result.engine === "typesafe"
      ? [probabilityColumn, ...baseColumns]
      : baseColumns;
  if (result) console.log("Recipe QA", result);

  let modal;
  if (isModalVisible) {
    modal = (
      <EuiModal onClose={() => setIsModalVisible(false)} maxWidth={1100}>
        <EuiModalHeader>
          <EuiModalHeaderTitle>
            <h1>
              {ENGINES[engine].label} QA{result ? `: ${result.name}` : ""}
            </h1>
          </EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          {loading && (
            <>
              <EuiProgress size="xs" color="accent" />
              <EuiText size="s">Reviewing recipe…</EuiText>
            </>
          )}
          {error && (
            <EuiCallOut title="QA failed" color="danger" iconType="alert">
              {error.message}
            </EuiCallOut>
          )}
          {result && !loading && (
            <>
              <EuiFlexGroup gutterSize="s" wrap responsive={false}>
                {result.counts
                  .filter((c) => c.count > 0)
                  .map((c) => (
                    <EuiFlexItem grow={false} key={c.category}>
                      <EuiBadge color="primary">
                        {c.category}: {c.count}
                      </EuiBadge>
                    </EuiFlexItem>
                  ))}
              </EuiFlexGroup>
              <EuiSpacer size="m" />
              {result.totalIssues === 0 ? (
                <EuiCallOut title="No issues found" color="success" iconType="check" />
              ) : (
                <EuiBasicTable items={result.issues} columns={columns} />
              )}
              <EuiSpacer size="s" />
              <EuiText size="xs" color="subdued">
                {result.totalIssues} issue(s) · model {result.model}
                {result.checks ? ` · ${result.checks} checks` : ""}
                {result.inputTokens ? ` · ${result.inputTokens} tokens` : ""}
                {result.durationMs ? ` · ${result.durationMs} ms` : ""}
              </EuiText>
            </>
          )}
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButton
            onClick={() =>
              refetch
                ? refetch({ id, engine })
                : runQA({ variables: { id, engine } })
            }
            isLoading={loading}
          >
            Re-run
          </EuiButton>
          <EuiButton onClick={() => setIsModalVisible(false)} fill>
            Close
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    );
  }

  return (
    <>
      <EuiPopover
        button={
          <EuiButton
            size="s"
            iconType="arrowDown"
            iconSide="right"
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            isLoading={loading}
          >
            QA
          </EuiButton>
        }
        isOpen={isPopoverOpen}
        closePopover={() => setIsPopoverOpen(false)}
        panelPaddingSize="none"
        anchorPosition="downLeft"
      >
        <EuiContextMenuPanel
          size="m"
          items={Object.entries(ENGINES).map(([key, { label, icon }]) => (
            <EuiContextMenuItem key={key} icon={icon} onClick={() => open(key)}>
              {label}
            </EuiContextMenuItem>
          ))}
        />
      </EuiPopover>
      {modal}
    </>
  );
}
